import { zodResolver } from '@hookform/resolvers/zod';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Platform, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { z } from 'zod';

import { useLoginCustomerForgotPassword } from '@/api/customers/use-login-customer-forgot-password';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { Button, ControlledInput, showErrorMessage, Text } from '@/ui';
import { Eon } from '@/ui/icons/eon';
import { Loader } from '@/ui/loader';

import ForgotPasswordSent from '../forgot-password-sent';

const forgotPasswordFormSchema = z.object({
  email: z.string().email(),
});

export type ForgotPasswordFormType = z.infer<typeof forgotPasswordFormSchema>;

export type ForgotPasswordFormProps = {
  email: string;
};

interface ForgotPasswordProps {
  onBack: () => void;
  onDone: () => void;
}

export default function ForgotPassword({
  onBack,
  onDone,
}: ForgotPasswordProps) {
  const { styles, theme } = useStyles(stylesheet);
  const [showForgotPasswordSent, setShowForgotPasswordSent] = useState(false);

  const {
    mutate: loginCustomerForgotPass,
    isPending: isSendingForgotPassword,
  } = useLoginCustomerForgotPassword();

  const {
    handleSubmit,
    control,
    trigger,
    setValue,
    formState: { isValid },
  } = useForm<ForgotPasswordFormType>({
    resolver: zodResolver(forgotPasswordFormSchema),
  });

  const handleContinue = (data: ForgotPasswordFormProps) => {
    loginCustomerForgotPass(
      { ...data },
      {
        onSuccess: () => {
          setShowForgotPasswordSent(true);
        },
        onError: (e) => {
          showErrorMessage(e.message);
        },
      }
    );
  };

  if (isSendingForgotPassword) {
    return <Loader />;
  }

  const renderBody = () => {
    if (showForgotPasswordSent) {
      return (
        <ForgotPasswordSent
          onBack={() => setShowForgotPasswordSent(false)}
          onDone={() => {
            setShowForgotPasswordSent(false);
            onDone && onDone();
          }}
        />
      );
    }

    return (
      <View style={{ flexGrow: 1, backgroundColor: theme.colors.background_2 }}>
        <View style={styles.formContainer}>
          <NavigationButton
            type={NavigationButtonTypes.back_with_text}
            onPress={() => {
              onBack();
            }}
          />

          {Platform.OS !== 'web' && <Eon width="48" height="18" />}

          <Text tx="forgotPassword.subtitle" type="title1" />

          <Text tx="forgotPassword.body" type="bodyRegular" />

          <ControlledInput
            control={control}
            name="email"
            label="Email"
            spellCheck={false}
            onBlur={() => {
              trigger('email');
            }}
            onChangeText={(text) => {
              trigger('email');
              setValue('email', text);
            }}
            autoComplete="email"
          />

          <View style={styles.buttonContainer}>
            <Button
              tx="forgotPassword.send"
              type="primary"
              onPress={handleSubmit(handleContinue)}
              disabled={isValid === false}
            />
          </View>
        </View>
      </View>
    );
  };

  return (
    <View
      style={{
        flexGrow: 1,
        borderRadius: 16,
        backgroundColor: theme.colors.background_2,
      }}
    >
      <View style={styles.container}>{renderBody()}</View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flexGrow: 1,
    gap: 16,
    alignItems: 'center',
  },
  formContainer: {
    flexGrow: 1,
    paddingLeft: 24,
    paddingRight: 24,
    paddingBottom: 24,
    gap: 16,
    marginTop: 16,
  },
  inputsContainer: {
    gap: 16,
  },
  buttonContainer: {
    gap: 16,
    alignItems: 'center',
  },
}));
